import { Modal } from 'antd'
import { Button as AntDButton, Form, Input, Radio, Tag } from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { useMutation } from '@tanstack/react-query';
import HTTPClient from 'services/HTTPClient';
import { ENABLED_TOPICS, showErrorToast, showSuccessToast } from 'utils';
import Button from 'components/Button';
import useTopic from 'components/useTopic';
import { ReactComponent as RocketIcon } from '../resources/images/rocket_launch.svg';
import useGrade from 'components/useGrade';
import { useState } from 'react';
import { QuestionTemplate, QuestionTemplateView } from './CurateQuestionsPage';
import { assignQuestions } from './AssignQuestionsModal';
import useGenerateImage from 'components/GenerateImage';
import useBookRangeSelector from 'components/useBookRangeSelector';

const formItemLayoutWithOutLabel = {
    wrapperCol: {
        xs: { span: 24, offset: 0 },
        sm: { span: 20, offset: 0 },
    },
};

const useGenerateKeyPoints = () => {
    return useMutation({
        mutationFn: async ({ grade, passage, topic, bookURL }: { grade: string, passage: string, topic: string, bookURL?: string }) => {
            if (!passage && !topic) {
                showErrorToast("Passage or Topic is required!")
                return
            }
            if (!grade) {
                showErrorToast("Grade is required!")
                return
            }
            console.log("Generating Key points", passage, topic)
            try {
                const url = `/api/tenant/key_points`
                let data = {
                    passage,
                    topic,
                    grade,
                    bookURL,
                }
                console.log(data)
                let response = await HTTPClient.get().postDataForJSONResult(url, data)
                showSuccessToast("Done")
                return response.keypoints
            } catch (error: any) {
                showErrorToast(error.message)
            }
        }
    })
}

const useGeneratePassage = () => {
    return useMutation({
        mutationFn: async ({ grade, topic, key_points, bookURL }: { grade: string, topic: string, key_points, bookURL?: string }) => {
            if (!topic) {
                showErrorToast("Topic is required!")
                return
            }
            if (!key_points) {
                showErrorToast("Key points are required!")
                return
            }
            if (!grade) {
                showErrorToast("Grade is required!")
            }
            console.log("Generating Passage", topic, key_points)
            try {
                const url = `/api/tenant/passage`
                let data = {
                    topic,
                    key_points,
                    grade,
                    bookURL,
                }
                console.log(data)
                let response = await HTTPClient.get().postDataForJSONResult(url, data)
                showSuccessToast("Done")
                return response.passage
            } catch (error: any) {
                showErrorToast(error.message)
            }
        }
    })
}

const useGenerateQuestions = () => {
    return useMutation({
        mutationFn: async ({ passage, grade, topic }: { passage: string, grade?: string, topic: string }): Promise<QuestionTemplate[]> => {
            if (!passage) {
                showErrorToast("Passage required!")
                return []
            }
            if (!topic) {
                showErrorToast("topic required!")
                return []
            }
            if (!grade) {
                showErrorToast("grade required!")
                return []
            }
            console.log("Generating Questions", passage)
            try {
                const url = `/api/tenant/generateQuestions`
                let data = {
                    passage,
                    grade,
                    topic
                }
                console.log(data)
                let response = await HTTPClient.get().postDataForJSONResult(url, data)
                showSuccessToast("Done")
                return response
            } catch (error: any) {
                showErrorToast(error.message)
                return []
            }
        }
    })
}

const useMutateAssignLessonPlan = () => {
    type Params = { users, topic, grade, questions, title, passage, key_points, image }
    return useMutation({
        mutationFn: async ({ users, title, passage, key_points, topic, grade, questions, image }: Params) => {
            if (!ENABLED_TOPICS.includes(topic)) {
                showErrorToast(`The selected topic '${topic}' is not currently enabled in the app!`)
                return
            }
            if (!grade) {
                showErrorToast("Please select a grade!")
            }
            if (!title) {
                showErrorToast("Topic required!")
                return
            }
            if (!key_points || key_points.length < 5) {
                showErrorToast("Atleast 5 key points required!")
                return
            }
            if (!passage) {
                showErrorToast("Passage required!")
                return
            }
            if (questions.length < 3) {
                showErrorToast("Please generate & approve atleast 3 questions!")
                return
            }
            console.log("Assigning Lesson Plan..", users)
            try {
                const url = `/api/tenant/lessonPlan`
                let data = {
                    user_ids: users.map(u => u.id),
                    topic,
                    grade,
                    questions,
                    passage,
                    title,
                    key_points,
                    image,
                }
                console.log(data)
                let response = await HTTPClient.get().postDataForJSONResult(url, data)
                // let assignQuestionsResponse = await assignQuestions({ users, questions, topic })
                // console.log(response, assignQuestionsResponse)
                showSuccessToast("Done")
                return response
            } catch (error: any) {
                showErrorToast(error.message)
            }
        }
    })
}

export default function AssignLessonPlanModal({ users, onClose }) {
    const [form] = Form.useForm();
    let [topic, topicSelectorRenderer] = useTopic();
    let [grade, gradeSelectorRenderer] = useGrade();
    let [bookURL, bookSelectorRenderer] = useBookRangeSelector();
    const [questions, setQuestions] = useState<QuestionTemplate[]>([])
    const [image, generatedImageRenderer, generateImage] = useGenerateImage();

    const { isLoading: isAssigning, mutateAsync: assignLessonPlan } = useMutateAssignLessonPlan()
    const { isLoading: isGeneratingKeyPoints, mutateAsync: generateKeyPoints } = useGenerateKeyPoints()
    const { isLoading: isGeneratingPassage, mutateAsync: generatePassage } = useGeneratePassage()
    const { isLoading: isGeneratingQuestions, mutateAsync: generateQuestions } = useGenerateQuestions()


    console.log(form.getFieldValue("topic"), form.getFieldValue("points"), form.getFieldValue("passage"))

    return (
        <Modal
            open
            onCancel={onClose}
            footer={null}
            width="80vw"
            className='assign-lesson-plan-modal'
        >
            <h3 className='is-size-3 mb-5 between'>
                <strong>
                    Assign Lesson Plan
                </strong>
                {generatedImageRenderer()}
            </h3>
            <div>
                <div className='mb-5'>
                    <div>
                        <Form
                            form={form}
                            layout="vertical"
                        >
                            <div className='mb-3'>
                                <p className='mb-2'>
                                    Select a topic, grade
                                </p>
                                <div className='d-flex mb-3'>
                                    <div className='mr-2'>
                                        {topicSelectorRenderer()}
                                    </div>
                                    <div>
                                        {gradeSelectorRenderer()}
                                    </div>
                                </div>
                            </div>
                            <div className='mb-3'>
                                <p className='mb-2'>
                                    Select a source (optional)
                                </p>
                                <div className='mb-3'>
                                    {bookSelectorRenderer()}
                                </div>
                            </div>
                            <div className='my-6' />
                            <Form.Item
                                label={
                                    <div className='between w-100'>
                                        Topic for discussion
                                        <Button
                                            variant='is-light'
                                            className='ml-2'
                                            onClick={async () => {
                                                generateImage(form.getFieldValue("topic"))
                                            }}
                                        >
                                            Generate image
                                            <RocketIcon className='ml-2' />
                                        </Button>
                                    </div>
                                }
                                required
                                name="topic"
                            >
                                <Input placeholder="Example: Types of natural disasters" />
                            </Form.Item>
                            <div className='mb-5 between'>
                                <div className='mr-2'>
                                    Key points to discuss - add minimum 5
                                </div>
                                <Button
                                    isLoading={isGeneratingKeyPoints}
                                    variant='is-light'
                                    onClick={async () => {
                                        let keyPoints = await generateKeyPoints({
                                            passage: form.getFieldValue("passage"),
                                            topic: form.getFieldValue("topic"),
                                            grade: grade!,
                                            bookURL
                                        })
                                        form.setFieldValue("points", keyPoints)
                                    }}
                                >
                                    Generate based on topic
                                    <RocketIcon className='ml-2' />
                                </Button>
                            </div>
                            <Form.List
                                name="points"
                                rules={[
                                    {
                                        validator: async (_, names) => {
                                            if (!names || names.length < 5) {
                                                return Promise.reject(new Error('At least 5 points'));
                                            }
                                        },
                                    },
                                ]}
                            >
                                {(fields, { add, remove }, { errors }) => (
                                    <>
                                        {fields.map((field, index) => (
                                            <Form.Item
                                                {...(formItemLayoutWithOutLabel)}
                                                required={false}
                                                key={field.key}
                                            >
                                                <Form.Item
                                                    {...field}
                                                    validateTrigger={['onChange', 'onBlur']}
                                                    rules={[
                                                        {
                                                            required: true,
                                                            whitespace: true,
                                                            message: "Please input a key point or delete this field.",
                                                        },
                                                    ]}
                                                    noStyle
                                                >
                                                    <Input placeholder="Example: Volcanoes" style={{ width: '60%' }} />
                                                </Form.Item>
                                                <MinusCircleOutlined
                                                    className="dynamic-delete-button ml-2"
                                                    onClick={() => remove(field.name)}
                                                />
                                            </Form.Item>
                                        ))}
                                        <div className='d-flex'>
                                            <Form.Item>
                                                <AntDButton
                                                    type="dashed"
                                                    onClick={() => add()}
                                                    icon={<PlusOutlined />}
                                                >
                                                    Add a point
                                                </AntDButton>
                                                <Form.ErrorList errors={errors} />
                                            </Form.Item>
                                        </div>
                                    </>
                                )}
                            </Form.List>
                            <Form.Item
                                name="passage"
                                label={
                                    <div className='between w-100'>
                                        Passage
                                        <Button
                                            isLoading={isGeneratingPassage}
                                            variant='is-light'
                                            className='ml-2'
                                            onClick={async () => {
                                                let passage = await generatePassage({
                                                    key_points: form.getFieldValue("points"),
                                                    topic: form.getFieldValue("topic"),
                                                    grade: grade!,
                                                    bookURL
                                                })
                                                form.setFieldValue("passage", passage)
                                            }}
                                        >
                                            Generate based on key points
                                            <RocketIcon className='ml-2' />
                                        </Button>
                                    </div>
                                }
                                rules={[{ required: true, message: 'Please paste the complete passage' }]}
                            >
                                <Input.TextArea
                                    showCount
                                    className='passage-text-area'
                                    placeholder='A natural disaster is a major event caused by natural processes of the Earth. Examples include floods, hurricanes, tornadoes, volcanic eruptions, earthquakes, tsunamis etc.'
                                />
                            </Form.Item>

                            <Form.Item
                                label={
                                    <div className='between w-100'>
                                        Questions
                                        <Button
                                            isLoading={isGeneratingQuestions}
                                            variant='is-light'
                                            onClick={async () => {
                                                let _questions = await generateQuestions({
                                                    passage: form.getFieldValue("passage"),
                                                    topic,
                                                    grade,
                                                })
                                                console.log(_questions)
                                                form.setFieldValue("questions", _questions)
                                                let newQuestions = [...questions, ..._questions]
                                                setQuestions(newQuestions)
                                            }}
                                        >
                                            Generate Questions
                                            <RocketIcon className='ml-2' />
                                        </Button>
                                    </div>
                                }
                                required
                                name="questions"
                            >
                                {
                                    questions?.map((qt, index) => {
                                        return <QuestionTemplateView
                                            key={qt.id}
                                            qt={qt}
                                            index={index}
                                        />
                                    })
                                }
                            </Form.Item>
                            <Form.Item>
                                <Button isLoading={isAssigning} onClick={() => assignLessonPlan({
                                    users,
                                    title: form.getFieldValue("topic"),
                                    key_points: form.getFieldValue("points").map(kp => ({ key_point: kp })),
                                    passage: form.getFieldValue("passage"),
                                    topic,
                                    grade,
                                    image,
                                    questions: questions.filter(q => q.is_verified).map(q => {
                                        return { ...q, options: q.options?.filter(o => o).map(o => ({ label: o })) }
                                    })
                                })} >
                                    Assign
                                </Button>
                            </Form.Item>
                        </Form>
                    </div>
                </div>
            </div>
        </Modal>
    )
}